import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import type { AssetRecord } from '../api/ame-platform.api';
import { getAssetSignedUrl } from '../api/ame-platform.api';
import { AssetStatusBadge } from './AssetStatusBadge';
import { FileSizeDisplay } from './FileSizeDisplay';

function MetaRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-3 border-b border-border py-2 last:border-0">
      <span className="w-36 shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className="min-w-0 break-all text-xs text-foreground">{value}</span>
    </div>
  );
}

interface AssetMetadataModalProps {
  asset: AssetRecord;
  onClose: () => void;
}

export function AssetMetadataModal({ asset, onClose }: AssetMetadataModalProps) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [urlLoading, setUrlLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (asset.assetStatus === 'ACTIVE') {
      setUrlLoading(true);
      getAssetSignedUrl(asset.id)
        .then((url) => {
          if (!cancelled) setSignedUrl(url);
        })
        .catch(() => undefined)
        .finally(() => {
          if (!cancelled) setUrlLoading(false);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [asset.id, asset.assetStatus]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-lg rounded-xl border border-border bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <h2 className="text-sm font-semibold text-foreground">Asset Details</h2>
            <div className="mt-1 flex items-center gap-2">
              <AssetStatusBadge status={asset.assetStatus} />
              <span className="truncate text-xs text-muted-foreground">
                {asset.originalFileName}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-5 py-2">
          <MetaRow
            label="Asset ID"
            value={<span className="font-mono text-[10px]">{asset.id}</span>}
          />
          <MetaRow label="Module" value={asset.moduleId} />
          <MetaRow label="Asset Class" value={asset.assetClass} />
          <MetaRow label="Entity Type" value={asset.entityType} />
          <MetaRow label="Entity ID" value={asset.entityId ?? '—'} />
          <MetaRow label="MIME Type" value={asset.mimeType} />
          <MetaRow label="File Size" value={<FileSizeDisplay bytes={asset.fileSizeBytes} />} />
          <MetaRow label="Version" value={`v${asset.currentVersionNumber}`} />
          <MetaRow label="Dedup Copy" value={asset.isDedupCopy ? 'Yes' : 'No'} />
          <MetaRow label="Tenant ID" value={asset.tenantId ?? 'Platform'} />
          <MetaRow
            label="Uploaded By"
            value={<span className="font-mono text-[10px]">{asset.uploadedById}</span>}
          />
          <MetaRow
            label="Provider ID"
            value={<span className="font-mono text-[10px]">{asset.providerId}</span>}
          />
          <MetaRow
            label="Checksum SHA-256"
            value={
              <span className="font-mono text-[10px]">{asset.checksumSha256.slice(0, 32)}…</span>
            }
          />
          <MetaRow label="Created" value={new Date(asset.createdAt).toLocaleString()} />
        </div>

        <div className="flex items-center justify-between border-t border-border px-5 py-3">
          <div>
            {asset.assetStatus === 'ACTIVE' && (
              <>
                {urlLoading && <div className="h-4 w-32 animate-pulse rounded bg-muted" />}
                {!urlLoading && signedUrl && (
                  <a
                    href={signedUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-medium text-primary underline underline-offset-2"
                  >
                    Preview (signed URL, 1h)
                  </a>
                )}
              </>
            )}
          </div>
          <button
            onClick={onClose}
            className="rounded-lg bg-muted px-4 py-1.5 text-sm font-medium text-foreground hover:bg-muted/70 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
